/* ==========================================================================
   FurrFlora: first-visit welcome popup
   --------------------------------------------------------------------------
   1. A FurrFlora diffuser plugged into the wall asks to be switched on.
   2. Tapping it turns it on, and a cloud of smoke fills the popup...
   3. ...then clears to reveal 15% off the first order for joining the mailing list.
   It opens on whichever page the visitor is on, 10 seconds after that page loads,
   once per visitor. However they leave the popup (signed up or not),
   their code goes into a gift button (bottom right, every page, every later visit) that
   brings it back at any time.
   Add ?welcome to any page URL to preview the popup now.
   ========================================================================== */
const WELCOME_CONFIG = {
  code: 'CALM15',   // Shopify admin → Discounts: 15% off one-time purchases, one use per customer (owner, 2026-10-26; was CALM10, 10%)
  percent: 15,
  // Your email provider's signup form address (Klaviyo, Mailchimp, Shopify Forms, etc.).
  // Leave empty for demo mode: the code is still revealed, but no email is sent anywhere.
  signupUrl: '',
  showAfterSeconds: 10, // counted from when the page loads
  homepageOnly: false,  // true = only open it on the homepage (/, marked with data-home)
  // Raise this number to give EVERY visitor a fresh start: the popup shows again, and saved codes
  // and the gift button are cleared. Handy while testing; avoid changing it after launch.
  resetVersion: 6,
};

// shared with the footer newsletter: send an email address to the mailing list
window.ffSubscribe = (email, source) => {
  try { localStorage.setItem('furrflora-discount', WELCOME_CONFIG.code); } catch { /* private mode */ }
  // Shopify theme: Shopify's own newsletter form. The email joins the store's customer list, subscribed to
  // marketing (Shopify Email can reach it), tagged with where it came from (owner, 2026-10-21).
  if (window.FF_THEME) {
    return fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ form_type: 'customer', utf8: '✓', 'contact[email]': email, 'contact[tags]': `newsletter,${source}`, 'contact[accepts_marketing]': 'true' }),
    }).catch(() => {});
  }
  if (!WELCOME_CONFIG.signupUrl) return Promise.resolve();
  return fetch(WELCOME_CONFIG.signupUrl, {
    method: 'POST', mode: 'no-cors',
    body: new URLSearchParams({ email, source, tags: 'website,' + source }),
  }).catch(() => {});
};
window.ffWelcomeCode = WELCOME_CONFIG;

(() => {
  const KEY = 'furrflora-welcome';
  const store = {
    get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
    del: (k) => { try { localStorage.removeItem(k); } catch { /* private mode */ } },
  };
  // fresh start for everyone when resetVersion goes up
  if (store.get('furrflora-welcome-reset') !== String(WELCOME_CONFIG.resetVersion)) {
    ['furrflora-welcome', 'furrflora-welcome-v2', 'furrflora-welcome-v3', 'furrflora-gift', 'furrflora-discount'].forEach(store.del);
    store.set('furrflora-welcome-reset', String(WELCOME_CONFIG.resetVersion));
  }
  // visitors who saved the old CALM10 code get the new one (owner moved to 15% off, 2026-10-26)
  if (store.get('furrflora-discount') === 'CALM10') { store.set('furrflora-discount', WELCOME_CONFIG.code); document.dispatchEvent(new CustomEvent('ff:discount')); }
  const forced = /[?&]welcome\b/.test(location.search);
  const seen = store.get(KEY);
  const onHome = !WELCOME_CONFIG.homepageOnly || Boolean(document.querySelector('[data-home]'));
  // anyone who has been through the popup has their code saved, and keeps the gift button for good
  const hasGift = Boolean(store.get('furrflora-discount'));
  const autoOpen = forced || (onHome && !seen && !hasGift);

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const { code, percent } = WELCOME_CONFIG;
  const markSeen = () => { if (store.get(KEY) !== 'joined') store.set(KEY, 'seen'); };

  const diffuserSVG = `
    <svg class="ffw-diffuser-art" viewBox="0 0 140 172" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="ffw-shell" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#FFFFFF"/><stop offset="1" stop-color="#ECE5DA"/></linearGradient>
        <linearGradient id="ffw-amber" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#F3DFAE"/><stop offset=".5" stop-color="#FBEFD0"/><stop offset="1" stop-color="#E2C07A"/></linearGradient>
        <radialGradient id="ffw-halo"><stop offset="0" stop-color="#FFF4D6" stop-opacity=".95"/><stop offset=".55" stop-color="#E9F0DA" stop-opacity=".55"/><stop offset="1" stop-color="#E9F0DA" stop-opacity="0"/></radialGradient>
      </defs>
      <circle class="ffw-halo" cx="70" cy="86" r="78" fill="url(#ffw-halo)"/>
      <g class="ffw-wisps">
        <g class="w w1"><path d="M66 40 C 58 28, 74 20, 66 6 C 60 -4, 70 -12, 66 -22" stroke="#D2BE9F" opacity=".7" transform="translate(2 2)"/><path d="M66 40 C 58 28, 74 20, 66 6 C 60 -4, 70 -12, 66 -22" stroke="#FFFDF8"/></g>
        <g class="w w2"><path d="M76 40 C 84 30, 70 22, 78 10 C 84 0, 76 -8, 80 -16" stroke="#D2BE9F" opacity=".7" transform="translate(2 2)"/><path d="M76 40 C 84 30, 70 22, 78 10 C 84 0, 76 -8, 80 -16" stroke="#FFFDF8"/></g>
      </g>
      <ellipse cx="70" cy="166" rx="34" ry="4" fill="#5C4A2E" opacity=".14"/>
      <circle cx="44" cy="48" r="11" fill="#F7F3EC" stroke="#E2D7C6" stroke-width="1.5"/>
      <circle cx="96" cy="48" r="11" fill="#F7F3EC" stroke="#E2D7C6" stroke-width="1.5"/>
      <path d="M18 96 C18 62 40 40 70 40 C100 40 122 62 122 96 C122 118 106 130 70 130 C34 130 18 118 18 96 Z" fill="url(#ffw-shell)" stroke="#E2D7C6" stroke-width="1.5"/>
      <path d="M21 104 C 38 113, 102 113, 119 104" fill="none" stroke="#E4DACB" stroke-width="1.5"/>
      <path d="M58 50 q12 -5 24 0" fill="none" stroke="#E4DACB" stroke-width="2" stroke-linecap="round"/>
      <circle class="ffw-led" cx="70" cy="119" r="3.2"/>
      <ellipse class="ffw-switch" cx="106" cy="88" rx="12" ry="8.5" transform="rotate(-24 106 88)"/>
      <ellipse cx="103" cy="85" rx="4.5" ry="2.2" transform="rotate(-24 103 85)" fill="#fff" opacity=".55"/>
      <rect x="58" y="128" width="24" height="7" rx="2" fill="#C9A15A"/>
      <rect x="40" y="133" width="60" height="34" rx="10" fill="url(#ffw-amber)" stroke="#D9B86E" stroke-width="1"/>
      <rect x="46" y="137" width="5" height="26" rx="2.5" fill="#fff" opacity=".55"/>
      <rect x="55" y="139" width="34" height="22" rx="3" fill="#FCF7EC"/>
      <path d="M66 150 c2 -6 9 -7 12 -5 c-2 5 -7 8 -12 5 z" fill="#7E9B5F"/>
      <rect x="61" y="154" width="22" height="2" rx="1" fill="#2B3417" opacity=".55"/>
    </svg>`;

  const sleepyCat = `
    <svg class="ffw-cat" viewBox="0 0 120 60" aria-hidden="true" focusable="false">
      <path d="M18 52 C 4 52, 4 34, 20 36 C 28 37, 26 46, 18 46" fill="none" stroke="#D9B27A" stroke-width="7" stroke-linecap="round"/>
      <ellipse cx="58" cy="42" rx="38" ry="16" fill="#E3BA80"/>
      <path d="M40 30 c6 -4 14 -4 18 0 M50 28 c6 -5 14 -5 18 0" stroke="#D49F5C" stroke-width="2.4" fill="none" stroke-linecap="round" opacity=".7"/>
      <g class="ffw-cat-head">
        <path d="M80 26 L84 12 L92 22 Z M100 22 L108 12 L110 27 Z" fill="#E3BA80" stroke="#E3BA80" stroke-width="3" stroke-linejoin="round"/>
        <circle cx="95" cy="35" r="15" fill="#E8C38E"/>
        <path d="M87 35 q3 2.5 6 0 M98 35 q3 2.5 6 0" stroke="#5C4A2E" stroke-width="1.8" fill="none" stroke-linecap="round"/>
        <path d="M94.5 40 l1.5 1.5 l1.5 -1.5 z" fill="#C98A7A"/>
      </g>
      <ellipse cx="72" cy="54" rx="9" ry="4.5" fill="#EFCF9F"/>
    </svg>`;

  const root = document.createElement('div');
  root.className = 'ffw';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-labelledby', 'ffw-t1');
  root.innerHTML = `
    <div class="ffw-backdrop" data-ffw-close></div>
    <div class="ffw-card">
      <button class="ffw-min" type="button" aria-label="Minimize. Your code waits in the gift button" data-ffw-min><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12"/></svg></button>
      <button class="ffw-close" type="button" aria-label="Close" data-ffw-close><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      <div class="ffw-body">
        <section class="ffw-step" data-step="1">
          <div class="ffw-scene">
            <svg class="ffw-leafshadow" viewBox="0 0 120 120" aria-hidden="true" focusable="false"><g fill="#7A5F3C"><path d="M10 6c26 10 40 30 46 58-20-10-38-28-46-58Z"/><path d="M58 4c14 18 16 40 8 62-14-16-18-38-8-62Z"/><path d="M96 20c-4 22-18 38-38 48 2-22 16-40 38-48Z"/><path d="M2 54c22 0 40 12 50 30-22 4-40-6-50-30Z"/><path d="M56 70c4 20 0 36-12 48" stroke="#7A5F3C" stroke-width="3" fill="none"/></g></svg>
            <span class="ffw-socket" aria-hidden="true"><i></i><i></i></span>
            <button class="ffw-diffuser" type="button" aria-label="Turn on the FurrFlora diffuser" aria-pressed="false">
              ${diffuserSVG}
              <span class="ffw-bubble" aria-hidden="true">Tap me!</span>
              <span class="ffw-tap" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V10m0-.5V8a1.5 1.5 0 0 1 3 0v2m0-.5a1.5 1.5 0 0 1 3 0V14c0 3.5-2.5 6-6 6h-1c-2.2 0-3.6-.9-4.8-2.6L4.6 14a1.5 1.5 0 0 1 2.4-1.8L9 14"/></svg></span>
            </button>
            <svg class="ffw-plant" viewBox="0 0 60 80" aria-hidden="true" focusable="false">
              <path d="M30 46C22 34 10 30 4 32c4 10 14 16 26 14Z" fill="#9DB383"/><path d="M30 46c2-16 12-28 22-30 0 12-8 24-22 30Z" fill="#7E9B5F"/><path d="M30 46c-4-18-2-32 4-40 6 10 4 26-4 40Z" fill="#B7C99D"/>
              <path d="M14 46h32l-4 30H18Z" fill="#F4EAD9"/><path d="M14 46h32l-.8 6H14.8Z" fill="#D9B27A"/><path d="M18 76h24" stroke="#E2D2B6" stroke-width="2"/>
            </svg>
            ${sleepyCat}
            <span class="ffw-zz" aria-hidden="true"><b>z</b><b>z</b><b>z</b></span>
          </div>
          <div class="ffw-copy">
            <p class="eyebrow">A little welcome gift</p>
            <h2 class="ffw-title" id="ffw-t1" tabindex="-1">Switch on the calm</h2>
            <p class="ffw-text">There's a little gift waiting inside.</p>
            <button class="ffw-cta" type="button" data-ffw-on><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V10m0-.5V8a1.5 1.5 0 0 1 3 0v2m0-.5a1.5 1.5 0 0 1 3 0V14c0 3.5-2.5 6-6 6h-1c-2.2 0-3.6-.9-4.8-2.6L4.6 14a1.5 1.5 0 0 1 2.4-1.8L9 14"/></svg><span data-ffw-on-text>Tap to switch it on</span></button>
            <button class="ffw-skip" type="button" data-ffw-close>Maybe later</button>
          </div>
        </section>

        <section class="ffw-step" data-step="2" hidden>
          <div class="ffw-offer" aria-hidden="true">
            <span class="ffw-offer-wisp a"></span><span class="ffw-offer-wisp b"></span>
            <span class="ffw-offer-num">${percent}%</span>
            <span class="ffw-offer-off">off your first order</span>
          </div>
          <div class="ffw-copy">
            <p class="eyebrow">Calm unlocked</p>
            <h2 class="ffw-title" id="ffw-t2" tabindex="-1">Your welcome gift is ready</h2>
            <p class="ffw-text">Join the FurrFlora list for ${percent}% off your first order, plus calm-care tips and first looks at new scents.</p>
            <form class="ffw-form" novalidate>
              <label class="visually-hidden" for="ffw-email">Email address</label>
              <input id="ffw-email" name="email" type="email" autocomplete="email" inputmode="email" placeholder="you@example.com" required aria-describedby="ffw-msg">
              <button class="btn btn-block" type="submit">Reveal my code</button>
              <p class="ffw-msg" id="ffw-msg" aria-live="polite"></p>
            </form>
            <p class="ffw-fine">For new customers. By joining you agree to receive FurrFlora emails. Unsubscribe anytime. <a href="/?view=policies#privacy">Privacy</a></p>
            <button class="ffw-skip" type="button" data-ffw-close>Skip, just show me the code later</button>
          </div>
        </section>

        <section class="ffw-step" data-step="3" hidden>
          <div class="ffw-offer is-done" aria-hidden="true">
            <span class="ffw-offer-wisp a"></span><span class="ffw-offer-wisp b"></span>
            <svg class="ffw-heart" viewBox="0 0 24 24"><path d="M12 20s-7-4.3-8.8-8.6C1.8 8.1 3.9 4.8 7.2 4.8c1.9 0 3.3 1.1 4.8 2.8 1.5-1.7 2.9-2.8 4.8-2.8 3.3 0 5.4 3.3 4 6.6C19 15.7 12 20 12 20Z"/></svg>
          </div>
          <div class="ffw-copy">
            <p class="eyebrow" data-ffw-eyebrow>You're on the list</p>
            <h2 class="ffw-title" id="ffw-t3" tabindex="-1">Here's your code</h2>
            <div class="ffw-code">
              <span class="ffw-code-text">${code}</span>
              <button class="ffw-copy-btn" type="button" data-ffw-copy>Copy</button>
            </div>
            <p class="ffw-text">${percent}% off your first order. We've saved it, so it's added at checkout automatically.</p>
            <a class="btn btn-block" href="/collections/all">Start shopping</a>
            <button class="ffw-skip" type="button" data-ffw-min>Keep browsing</button>
            <p class="ffw-fine">Your code stays in the gift button at the bottom of your screen, whenever you come back.</p>
            <form class="ffw-join" novalidate data-ffw-join hidden>
              <label class="ffw-join-label" for="ffw-join-email">Want calm-care tips and first looks at new scents too?</label>
              <div class="ffw-join-row">
                <input id="ffw-join-email" name="email" type="email" autocomplete="email" inputmode="email" placeholder="you@example.com" required aria-describedby="ffw-join-msg">
                <button class="btn btn-small" type="submit">Join</button>
              </div>
              <p class="ffw-msg" id="ffw-join-msg" aria-live="polite"></p>
            </form>
          </div>
        </section>
      </div>
      <div class="ffw-smoke" aria-hidden="true"><div class="ffw-fog"></div></div>
    </div>`;

  const card = root.querySelector('.ffw-card');
  const smoke = root.querySelector('.ffw-smoke');
  const diffuser = root.querySelector('.ffw-diffuser');
  let lastFocus = null;
  let busy = false;

  // smoke puffs: the first ones rise straight out of the diffuser, later ones roll further out
  // and down until the whole card is filled
  const PUFFS = 22;
  for (let i = 0; i < PUFFS; i++) {
    const p = document.createElement('span');
    p.className = 'ffw-puff';
    const side = i % 2 ? 1 : -1;
    const spread = (i / (PUFFS - 1)) * 200 + ((i * 37) % 17);       // degrees away from straight up
    const a = (-90 + side * spread) * Math.PI / 180;
    const r = 60 + i * 11 + ((i * 53) % 30);
    p.style.cssText = `--dx:${Math.round(Math.cos(a) * r)}px;--dy:${Math.round(Math.sin(a) * r * 1.35 - 30)}px;--s:${(2.3 + ((i * 31) % 6) * 0.3).toFixed(2)};--r:${((i * 53) % 120) - 60}deg;--d:${i * 42}ms`;
    smoke.appendChild(p);
  }

  const show = (n) => {
    showStep(n);
    root.querySelector(`#ffw-t${n}`).focus({ preventScroll: true });
  };

  // the minimized popup: a gift button, bottom right, on every page until the visitor closes it
  const giftSVG = `<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <rect x="5" y="13" width="22" height="15" rx="2.5" fill="#E3BA80"/>
    <rect x="3.5" y="9" width="25" height="6" rx="2" fill="#F0CF99"/>
    <rect x="14.2" y="9" width="3.6" height="19" fill="#9DB383"/>
    <path d="M16 9C12 2 6 5 9.5 8.5 11 9.6 14 9.4 16 9ZM16 9c4-7 10-4 6.5-.5C21 9.6 18 9.4 16 9Z" fill="#B7C99D" stroke="#9DB383"/>
  </svg>`;
  let gift = null;
  const showGift = () => {
    if (!gift) {
      gift = document.createElement('button');
      gift.type = 'button';
      gift.className = 'ffw-gift';
      gift.setAttribute('aria-label', `Show my ${percent}% off code`);
      gift.innerHTML = `${giftSVG}<span class="ffw-gift-badge" aria-hidden="true">${percent}%</span>`;
      gift.addEventListener('click', () => open(3, true));
      document.body.appendChild(gift);
      // sit just above the mobile "Shop" / "Add to cart" bar whenever it slides up
      const bars = [...document.querySelectorAll('.buy-bar')];
      const place = () => {
        if (!gift) return;
        const bar = bars.find((b) => b.classList.contains('show') && getComputedStyle(b).display !== 'none');
        gift.style.bottom = bar ? `${Math.round(window.innerHeight - bar.offsetTop + 12)}px` : '';
      };
      bars.forEach((b) => new MutationObserver(place).observe(b, { attributes: true, attributeFilter: ['class'] }));
      window.addEventListener('resize', place, { passive: true });
      place();
    }
    gift.hidden = false;
  };
  const popGift = () => { gift.classList.remove('is-waiting', 'pop'); void gift.offsetWidth; gift.classList.add('pop'); };
  // card → gift (and back): how far the card's centre is from the gift's
  const aimAtGift = () => {
    const g = gift.getBoundingClientRect(), c = card.getBoundingClientRect();
    card.style.setProperty('--gx', `${Math.round(g.left + g.width / 2 - (c.left + c.width / 2))}px`);
    card.style.setProperty('--gy', `${Math.round(g.top + g.height / 2 - (c.top + c.height / 2))}px`);
  };

  const showStep = (n) => {
    root.querySelectorAll('.ffw-step').forEach((s) => { s.hidden = s.dataset.step !== String(n); });
    root.setAttribute('aria-labelledby', `ffw-t${n}`);
    root.dataset.step = n;
  };

  const open = (step = 1, fromGift = false) => {
    if (root.isConnected) return;
    root.classList.remove('is-open', 'to-gift', 'from-gift');
    showStep(step);
    if (step === 3) { root.classList.add('is-on'); syncCodeStep(); }
    document.body.appendChild(root);
    lastFocus = document.activeElement;
    document.documentElement.classList.add('ffw-open');
    if (fromGift && gift && !reduce.matches) { aimAtGift(); root.classList.add('from-gift'); }
    if (gift) gift.hidden = true;
    void root.offsetWidth; // apply the start styles first so the fade-in runs (no rAF: it can stall in background tabs)
    root.classList.add('is-open');
    setTimeout(() => (step === 3 ? root.querySelector('[data-ffw-copy]') : diffuser).focus({ preventScroll: true }), 60);
    if (step === 1) markSeen();
  };

  const close = (minimized = false) => {
    root.classList.remove('is-open');
    document.documentElement.classList.remove('ffw-open');
    const done = () => {
      root.remove();
      root.classList.remove('to-gift', 'from-gift');
      if (minimized && gift) { popGift(); gift.focus({ preventScroll: true }); }
      else if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    };
    if (reduce.matches) done(); else setTimeout(done, minimized ? 460 : 320);
  };

// the code step reads differently for visitors who skipped the signup
const joined = () => store.get(KEY) === 'joined';
const syncCodeStep = () => {
  root.querySelector('[data-ffw-eyebrow]').textContent = joined() ? "You're on the list" : 'Your welcome gift';
  root.querySelector('[data-ffw-join]').hidden = joined();
};

// shrink the popup into the gift button. However they leave, the code is saved and kept in the gift
const minimize = () => {
  if (!store.get('furrflora-discount')) {
    store.set('furrflora-discount', code);
    document.dispatchEvent(new CustomEvent('ff:discount'));
  }
  showGift();
    gift.classList.add('is-waiting');
    if (!reduce.matches) { aimAtGift(); root.classList.add('to-gift'); }
    close(true);
  };

  // tap the diffuser: light on, wisps, then the smoke screen and the reveal
  diffuser.addEventListener('click', () => {
    if (busy) return;
    busy = true;
    root.classList.add('is-on');
    const cta = root.querySelector('[data-ffw-on]');
    cta.disabled = true;
    root.querySelector('[data-ffw-on-text]').textContent = 'Switching on…';
    diffuser.setAttribute('aria-pressed', 'true');
    diffuser.setAttribute('aria-label', 'The diffuser is on');
    if (reduce.matches) { setTimeout(() => { if (root.dataset.step === '1') show(2); busy = false; }, 500); return; }
    const c = card.getBoundingClientRect();
    const d = diffuser.getBoundingClientRect();
    card.style.setProperty('--ox', `${Math.round(d.left + d.width / 2 - c.left)}px`);
    card.style.setProperty('--oy', `${Math.round(d.top + d.height * 0.22 - c.top)}px`);
    setTimeout(() => root.classList.add('is-smoking'), 520);
    setTimeout(() => { if (root.dataset.step === '1') show(2); }, 520 + 1750);   // swapped while the smoke is thickest
    setTimeout(() => { root.classList.remove('is-smoking'); busy = false; }, 520 + 3900);
  });

  root.querySelector('[data-ffw-on]').addEventListener('click', () => diffuser.click());

  root.querySelector('.ffw-form').addEventListener('submit', (e) => {
    e.preventDefault();
    const input = root.querySelector('#ffw-email');
    const msg = root.querySelector('.ffw-msg');
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim());
    input.setAttribute('aria-invalid', String(!ok));
    msg.textContent = ok ? '' : 'Please enter a valid email address, like you@example.com.';
    if (!ok) { input.focus(); return; }
    const btn = e.submitter || root.querySelector('.ffw-form .btn');
    btn.disabled = true;
    window.ffSubscribe(input.value.trim(), 'welcome-popup').finally(() => {
    store.set(KEY, 'joined');
    document.dispatchEvent(new CustomEvent('ff:discount'));
    syncCodeStep();
    show(3);
  });
});

// optional signup on the code step (for visitors who skipped it the first time)
root.querySelector('[data-ffw-join]').addEventListener('submit', (e) => {
  e.preventDefault();
  const form = e.currentTarget;
  const input = form.querySelector('input');
  const msg = form.querySelector('.ffw-msg');
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim());
  input.setAttribute('aria-invalid', String(!ok));
  msg.classList.toggle('is-ok', ok);
  msg.textContent = ok ? '' : 'Please enter a valid email address, like you@example.com.';
  if (!ok) { input.focus(); return; }
  form.querySelector('.btn').disabled = true;
  window.ffSubscribe(input.value.trim(), 'gift').finally(() => {
    store.set(KEY, 'joined');
    root.querySelector('[data-ffw-eyebrow]').textContent = "You're on the list";
    form.querySelector('.ffw-join-label').hidden = true;
    form.querySelector('.ffw-join-row').hidden = true;
    msg.textContent = "You're on the list. Thank you!";
  });
});

  root.querySelector('[data-ffw-copy]').addEventListener('click', (e) => {
    const b = e.currentTarget;
    const done = () => { b.textContent = 'Copied'; b.classList.add('is-copied'); setTimeout(() => { b.textContent = 'Copy'; b.classList.remove('is-copied'); }, 2000); };
    if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(code).then(done, done);
    else {
      const t = document.createElement('textarea');
      t.value = code; t.setAttribute('readonly', ''); t.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      t.remove(); done();
    }
  });

  root.addEventListener('click', (e) => {
    // every way out (✕, Maybe later, Keep browsing, tapping outside) tucks the code into the gift
    if (e.target.closest('[data-ffw-min], [data-ffw-close]')) minimize();
  });
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); minimize(); return; }
    if (e.key !== 'Tab') return;
    // keep keyboard focus inside the popup
    const f = [...root.querySelectorAll('button, a[href], input')].filter((el) => !el.closest('[hidden]') && !el.disabled);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  if (hasGift) showGift();
  // the footer signup shows the gift straight away too
  window.ffShowGift = () => { if (!root.isConnected && !(gift && !gift.hidden)) { showGift(); popGift(); } };
  if (autoOpen) {
    // wait until the visitor isn't mid-task: tab hidden, menu or cart open, or typing in a field
    const isBusy = () => document.hidden
      || document.querySelector('.drawer.open, .upsell-wrap, [data-menu-toggle][aria-expanded="true"]')
      || (document.activeElement && document.activeElement.matches('input, textarea, select'));
    const tryOpen = () => { if (!forced && isBusy()) setTimeout(tryOpen, 2000); else open(1); }; // ?welcome previews skip the wait
    // performance.now() = time since this page started loading
    const wait = forced ? 400 : Math.max(0, WELCOME_CONFIG.showAfterSeconds * 1000 - performance.now());
    setTimeout(tryOpen, wait);
  }
})();
